/**
 * Native bridge — thin wrappers around Capacitor plugins with graceful
 * web fallbacks so pages can call these APIs unconditionally.
 *
 * The Capacitor modules are dynamically imported so the web bundle
 * stays lean and the plugins only load when `Capacitor.isNativePlatform()`
 * is true (i.e. iOS + Android shells).
 */

// ---------- Detection ----------

export async function isNative(): Promise<boolean> {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// ---------- Camera ----------

export interface CapturedPhoto {
  dataUrl: string;      // `data:image/jpeg;base64,…`
  format: string;       // 'jpeg' | 'png'
  source: "camera" | "gallery" | "file";
}

/**
 * Prompts the user for a camera / gallery photo. Falls back to a native
 * `<input type="file" capture="environment">` on web.
 */
export async function takePhoto(): Promise<CapturedPhoto | null> {
  if (await isNative()) {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    try {
      const photo = await Camera.getPhoto({
        quality: 85,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt, // user picks Camera vs Gallery
        correctOrientation: true,
      });
      return {
        dataUrl: photo.dataUrl || "",
        format: photo.format || "jpeg",
        source: "camera",
      };
    } catch (e) {
      // User cancelled — return null, not an error.
      return null;
    }
  }
  // Web fallback — trigger hidden input.
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    (input as any).capture = "environment";
    input.style.display = "none";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () =>
        resolve({
          dataUrl: reader.result as string,
          format: file.type.split("/")[1] || "jpeg",
          source: "file",
        });
      reader.readAsDataURL(file);
    };
    document.body.appendChild(input);
    input.click();
    setTimeout(() => input.remove(), 60_000);
  });
}

// ---------- Push notifications ----------

/**
 * Requests push permission, registers with APNs / FCM, and hands the token
 * to the backend so we can target this device. No-op on web — Web Push
 * is a separate flow via `pwa/registerSW.ts`.
 */
export async function initPushNotifications(
  registerToken: (payload: { platform: "ios" | "android"; push_token: string }) => Promise<void>,
): Promise<void> {
  if (!(await isNative())) return;
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const { Capacitor } = await import("@capacitor/core");
  const platform = (Capacitor.getPlatform() as "ios" | "android");

  const perm = await PushNotifications.checkPermissions();
  if (perm.receive !== "granted") {
    const req = await PushNotifications.requestPermissions();
    if (req.receive !== "granted") return; // user declined
  }

  await PushNotifications.register();

  PushNotifications.addListener("registration", async (t) => {
    try {
      await registerToken({ platform, push_token: t.value });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("device register failed:", e);
    }
  });

  PushNotifications.addListener("registrationError", (e) => {
    // eslint-disable-next-line no-console
    console.warn("push registration error:", e);
  });
}

// ---------- App-boot / deep-link routing ----------

/**
 * Wire Capacitor `App` events → React Router. Called once on app boot.
 * When the app is opened via a Universal Link / App Link, this converts
 * the incoming URL to the matching SPA path and pushes it into history.
 */
export async function initAppEvents(navigate: (path: string) => void): Promise<void> {
  if (!(await isNative())) return;
  const { App } = await import("@capacitor/app");

  App.addListener("appUrlOpen", (event) => {
    try {
      const url = new URL(event.url);
      // Preserve query + hash so `/contact/{id}?ref=…` still works.
      navigate(`${url.pathname}${url.search}${url.hash}` || "/");
    } catch {
      /* ignore malformed URLs */
    }
  });
}
